import { Loader2, MapPin } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    getApiClient,
    showTransparentRateLimitNotice,
} from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    getApiErrorDetail,
    isTransparentRateLimitError,
} from '@apps/web/lib/known_api_errors';
import { ApiError } from '@libraries/typescript/api_client';

type RecalcShiftDistancesButtonProps = {
    readonly organizationId: string;
    readonly shiftIds: ReadonlyArray<string>;
    readonly recommendedCaregiverIds: ReadonlyArray<string>;
    readonly onSuccess: () => void;
};

function firstGuardrailDetail(
    failures: ReadonlyArray<PromiseRejectedResult>,
): string | null {
    for (const failure of failures) {
        const error = failure.reason;
        if (
            isTransparentRateLimitError(error) ||
            !(error instanceof ApiError) ||
            error.status !== 429
        ) {
            continue;
        }
        const detail = getApiErrorDetail(error);
        if (detail !== null) {
            return detail;
        }
    }
    return null;
}

export function RecalcShiftDistancesButton({
    organizationId,
    shiftIds,
    recommendedCaregiverIds,
    onSuccess,
}: RecalcShiftDistancesButtonProps) {
    const [isRecalculating, setIsRecalculating] = useState(false);

    const handleClick = async () => {
        if (isRecalculating || shiftIds.length === 0) {
            return;
        }
        setIsRecalculating(true);
        try {
            // One re-geocode call per shift; caregivers go on every call so
            // a single shift's failure can't silently leave caregiver coords
            // stale.
            const caregiverPayload = [...recommendedCaregiverIds];
            const results = await Promise.allSettled(
                shiftIds.map((shiftId) =>
                    getApiClient().post({
                        url: '/organizations/{organization_id}/shifts/{shift_id}/recalculate-distances',
                        pathParams: {
                            organization_id: organizationId,
                            shift_id: shiftId,
                        },
                        body: { caregiver_ids: caregiverPayload },
                        // Recalculation replaces derived values, so retrying
                        // this POST cannot duplicate a user action.
                        idempotent: true,
                        rateLimitFailureMode: 'silent',
                    }),
                ),
            );
            const failures = results.filter(
                (result): result is PromiseRejectedResult =>
                    result.status === 'rejected',
            );
            if (failures.length === 0) {
                toast.success('Recalculated distances', {
                    persistNotification: false,
                });
                onSuccess();
                return;
            }
            const transparentFailures = failures.filter((failure) =>
                isTransparentRateLimitError(failure.reason),
            );
            const ordinaryFailures = failures.filter((failure) => {
                const error = failure.reason;
                return (
                    !isTransparentRateLimitError(error) &&
                    (!(error instanceof ApiError) || error.status !== 429)
                );
            });
            if (transparentFailures.length > 0) {
                await showTransparentRateLimitNotice();
            }
            const guardrailDetail = firstGuardrailDetail(failures);
            if (guardrailDetail !== null) {
                toast.error(guardrailDetail, {
                    persistNotification: false,
                });
                if (failures.length < shiftIds.length) {
                    onSuccess();
                }
                return;
            }
            if (
                ordinaryFailures.length === 0 &&
                transparentFailures.length === failures.length
            ) {
                if (failures.length < shiftIds.length) {
                    onSuccess();
                }
                return;
            }
            if (failures.length === shiftIds.length) {
                toast.error('Failed to recalculate distances', {
                    persistNotification: false,
                });
                return;
            }
            const succeeded = shiftIds.length - failures.length;
            toast.success(
                `Recalculated distances (${succeeded} of ${shiftIds.length} shifts)`,
                { persistNotification: false },
            );
            onSuccess();
        } finally {
            setIsRecalculating(false);
        }
    };

    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <Button
                    variant="ghost"
                    className="text-xs"
                    size="sm"
                    onClick={handleClick}
                    disabled={isRecalculating}
                    aria-busy={isRecalculating}
                    aria-label="Recalculate distances"
                >
                    {isRecalculating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                        <MapPin className="h-3 w-3" />
                    )}
                </Button>
            </TooltipTrigger>
            <TooltipContent>Recalculate distances</TooltipContent>
        </Tooltip>
    );
}
